import React, { useCallback, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { TableViewFilterProps } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import cn from 'classnames';
import { OutlineButton } from 'apps/sigma2-ui/src/components/ui/Button';
import { IconPaintCanSvg } from 'apps/sigma2-ui/src/components/svg/IconPaintCan';
import { useOnClickOutside } from 'apps/sigma2-ui/src/explore-section/hooks/useOnClickOutside';
import { TableColorTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableColorTooltip';

// eslint-disable-next-line complexity
export const TableViewColorFilter = (props: TableViewFilterProps) => {
  const { setViewConfiguration, viewConfiguration } = props;

  const language = useLanguage();

  const { baseColors: initialBaseColors } = viewConfiguration;

  const [baseColors, setBaseColors] = useState<string[] | undefined>(() => {
    return initialBaseColors ?? ['#F7F7F7', '#F7F7F7'];
  });

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const handleColorTooltipOpen = useCallback(() => {
    setIsColorPickerOpen(true);
  }, []);

  const handleColorTooltipClose = useCallback(() => {
    if (!isColorPickerOpen) {
      return;
    }

    // call with small delay to avoid race condition when user clicks on the open tooltip button
    setTimeout(() => {
      setIsColorPickerOpen(false);
      if (baseColors) {
        if (setViewConfiguration && viewConfiguration) {
          setViewConfiguration({
            ...viewConfiguration,
            baseColors: baseColors,
          });
        }
      }
    }, 50);
  }, [baseColors, isColorPickerOpen, setViewConfiguration, viewConfiguration]);

  const colorPickerRef = useOnClickOutside({
    handleClickOutside: handleColorTooltipClose,
  });

  const handleColorPickerChange = useCallback(
    (belowColor: string, aboveColor: string) => {
      const newBaseColors = [belowColor, aboveColor];
      setBaseColors(newBaseColors);
      if (setViewConfiguration && viewConfiguration) {
        setViewConfiguration({
          ...viewConfiguration,
          baseColors: newBaseColors,
        });
      }
    },
    [setViewConfiguration, viewConfiguration],
  );

  return (
    <LightTooltip
      open={isColorPickerOpen}
      arrow={true}
      placement="bottom"
      title={
        <div ref={colorPickerRef} className="min-w-72">
          <TableColorTooltip
            baseColors={baseColors ?? ['#F7F7F7', '#F7F7F7']}
            onColorChange={handleColorPickerChange}
          />
        </div>
      }
      className="[&>div]:!p-0"
      disableHoverListener={true}
      disableTouchListener={true}
    >
      <div>
        <OutlineButton
          behavior={{
            type: 'onClick',
            onClick: isColorPickerOpen
              ? handleColorTooltipClose
              : handleColorTooltipOpen,
          }}
          className="!mr-0"
          innerClassName={['border-gray-200']}
        >
          <div className="flex items-center gap-2">
            <IconPaintCanSvg svg={{ width: 16, height: 16 }} />
            <S2Typography
              variant={'body-7'}
              translatedContent={language.addColor()}
              textColorVariant="gray-900"
              dangerouslyOverrideClassNameFn={(className) =>
                cn(className, '!leading-[1.13rem]')
              }
            />
          </div>
        </OutlineButton>
      </div>
    </LightTooltip>
  );
};
